<template>
  <div class="error-page">
    <div class="error-page__frame">
      <div class="error-page__card">
        <a class="error-page__logo" href="https://rx2go.ai" target="_blank" rel="noopener noreferrer">
          <img src="@/assets/img/company-logo.svg" alt="Rx2GO">
        </a>
        <p v-if="errorCodeLabel" class="error-page__code">{{ errorCodeLabel }}</p>
        <h1 class="error-page__title">{{ errorTitle }}</h1>
        <p v-if="errorDescription" class="error-page__description">{{ errorDescription }}</p>
        <div
          class="error-page__decoration"
          :style="{ backgroundImage: `url(${errorImage})` }"
          aria-hidden="true"
        ></div>
        <p class="error-page__hint" v-html="hintText"></p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { phones } from '@/config'
import img400 from '@/assets/img/status/400.svg'
import img500 from '@/assets/img/status/500.svg'
import imgErr from '@/assets/img/status/err_bg.svg'

const props = defineProps({
  status: {
    type: [Number, String],
    default: null,
  },
  message: {
    type: String,
    default: '',
  },
})

const { t, te } = useI18n()

const STATUS_LABELS = {
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  408: 'Request Timeout',
  429: 'Too Many Requests',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
}

const statusCode = computed(() => {
  const code = Number(props.status)
  return Number.isFinite(code) && code > 0 ? code : null
})

const errorCodeLabel = computed(() => {
  if (statusCode.value === null) return null
  return t('error-page.error-code', { code: statusCode.value })
})

const errorTitle = computed(() => {
  const code = statusCode.value
  if (code !== null && te(`error-page.${code}.title`)) {
    return t(`error-page.${code}.title`)
  }
  if (code !== null && STATUS_LABELS[code]) {
    return STATUS_LABELS[code]
  }
  return t('error-page.generic.title')
})

const errorDescription = computed(() => {
  const code = statusCode.value
  if (code !== null && te(`error-page.${code}.description`)) {
    return t(`error-page.${code}.description`)
  }
  if (props.message) return props.message
  return t('error-page.generic.description')
})

const errorImage = computed(() => {
  const code = statusCode.value
  if (code === 400) {
    return img400
  }
  if (code === 500) {
    return img500
  }
  return imgErr
})

const hintText = computed(() => t('error-page.dispatcher-hint'))

const dispatcherPhoneHref = computed(() => {
  const phone = phones?.[2]?.trimmed
  return phone ? `tel:${phone}` : '#'
})
</script>

<style scoped lang="scss">
.error-page {
  min-height: 100vh;
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 0;
  background: #1fbec8;
  box-sizing: border-box;
  font-family: Arial, Helvetica, sans-serif;
}

.error-page__frame {
  width: 100%;
  max-width: 480px;
  min-height: 100vh;
  padding: 12px;
  box-sizing: border-box;
}

.error-page__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: calc(100vh - 24px);
  padding: 28px 20px 32px;
  text-align: center;
  background: #fff;
  border-radius: 16px;
  box-sizing: border-box;
}

.error-page__logo {
  display: block;
  margin-bottom: 24px;

  img {
    display: block;
    width: 120px;
    height: auto;
    margin: 0 auto;
  }
}

.error-page__code,
.error-page__title {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.25;
  color: #1fbec8;
}

.error-page__title {
  margin-bottom: 12px;
}

.error-page__description {
  margin: 0 0 8px;
  font-size: 20px;
  line-height: 1.45;
  color: #000;
}

.error-page__decoration {
  flex: 1;
  width: 100%;
  min-height: 180px;
  margin: 16px 0;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: contain;
}

.error-page__hint {
  margin: 0 0 20px;
  font-size: 18px;
  line-height: 1.45;
  color: #1fbec8;
}

.error-page__btn {
  display: block;
  width: 100%;
  padding: 16px 20px;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
  color: #fff;
  text-decoration: none;
  border-radius: 1000px;
  background: var(--color-confirm, linear-gradient(90deg, #9be13f 5%, #76c519 90%));
  box-sizing: border-box;
}
</style>
